// 面板緊急求救的觀看 App 提醒（docs/panel 07-dashboard §3.5、D11）。
// 推播還沒接 → 登入後全 App 範圍每 15 秒輪詢 GET /v3/me/panel-sos（AppShell 掛載 = 已登入時 start）；
// 分頁在背景暫停，回前景立刻讀一次。自己按過［我知道了］的 sos_id 記在 localStorage（依帳號分開）。
// 畫面：PanelSosAlert.tsx（全螢幕紅色提醒 + 小橫幅）。
import { ackPanelSos, currentUserId, listMyPanelSos, SOS_POLL_MS, sosAckGone, type PanelSos } from './panels';

const LS_PREFIX = 'ls.panelSos.acked.';
const LS_DISMISSED = 'ls.panelSos.dismissed.';
const LS_MAX = 50;

export interface PanelSosState {
  list: PanelSos[];
  /** 自己按過［我知道了］的 sos_id */
  mine: ReadonlySet<string>;
  /** 按了［看面板］先收成橫幅的（只在這次開著的頁面） */
  minimized: ReadonlySet<string>;
  /** 「你已回覆」橫幅按了［收起］的（D13；存 localStorage） */
  dismissed: ReadonlySet<string>;
  /** 正在送 ack 的 sos_id */
  acking: string | null;
  /** ack 失敗（網路 / 5xx）的 sos_id → 顯示「沒送出，請再試一次」 */
  ackFailed: string | null;
  /** 最近一次輪詢的時間（ms）；也讓「幾分鐘前」跟著輪詢重算 */
  fetchedAt: number;
}

const lsKey = (prefix = LS_PREFIX) => prefix + (currentUserId() ?? 'anon');

function loadMine(prefix = LS_PREFIX): Set<string> {
  try {
    const raw = globalThis.localStorage?.getItem(lsKey(prefix));
    const arr: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr.filter((v): v is string => typeof v === 'string') : []);
  } catch {
    return new Set();
  }
}

function saveMine(mine: ReadonlySet<string>, prefix = LS_PREFIX): void {
  try {
    globalThis.localStorage?.setItem(lsKey(prefix), JSON.stringify(Array.from(mine).slice(-LS_MAX)));
  } catch {
    /* 私密視窗 / 封鎖儲存：這次開著的頁面仍記得（state 裡），重開才會再跳 */
  }
}

const EMPTY: PanelSosState = { list: [], mine: new Set(), minimized: new Set(), dismissed: new Set(), acking: null, ackFailed: null, fetchedAt: 0 };

let state: PanelSosState = EMPTY;
const listeners = new Set<() => void>();
let timer: ReturnType<typeof setInterval> | null = null;
let inflight = false;
let generation = 0;

function set(patch: Partial<PanelSosState>) {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
}

const hidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';

async function poll(): Promise<void> {
  if (inflight || hidden()) return;
  inflight = true;
  const gen = generation;
  try {
    const list = await listMyPanelSos();
    if (gen !== generation) return; // 已登出 / 停止
    set({ list, fetchedAt: Date.now() });
  } catch {
    /* 讀不到：留著上一次的（斷網時提醒不會突然消失）；下一輪再試 */
  } finally {
    inflight = false;
  }
}

const onVisible = () => {
  if (!hidden()) void poll();
};

export const panelSos = {
  get: (): PanelSosState => state,
  subscribe(l: () => void): () => void {
    listeners.add(l);
    return () => listeners.delete(l);
  },

  /** 登入後開始輪詢；回傳停止函式（登出 / AppShell 卸載時呼叫） */
  start(): () => void {
    panelSos.stop();
    generation++;
    state = { ...EMPTY, mine: loadMine(), dismissed: loadMine(LS_DISMISSED) };
    void poll();
    timer = setInterval(() => void poll(), SOS_POLL_MS);
    document.addEventListener('visibilitychange', onVisible);
    return () => panelSos.stop();
  },

  stop(): void {
    if (timer) clearInterval(timer);
    timer = null;
    generation++;
    inflight = false;
    document.removeEventListener('visibilitychange', onVisible);
    state = EMPTY;
    listeners.forEach((l) => l());
  },

  /** 立刻讀一次（測試 / 按完 ack 後用） */
  refresh: poll,

  /** ［我知道了］/［我也知道了］：ack 成功 → 記成自己按過、換成雲端回的這筆；404 / 409 → 這筆拿掉 */
  async ack(sosId: string): Promise<void> {
    if (state.acking) return;
    const gen = generation;
    set({ acking: sosId, ackFailed: null });
    try {
      const updated = await ackPanelSos(sosId);
      if (gen !== generation) return;
      const mine = new Set(state.mine).add(sosId);
      saveMine(mine);
      set({ mine, acking: null, list: state.list.map((s) => (s.sos_id === sosId && updated ? updated : s)) });
    } catch (e) {
      if (gen !== generation) return;
      if (sosAckGone(e)) set({ acking: null, list: state.list.filter((s) => s.sos_id !== sosId) });
      else set({ acking: null, ackFailed: sosId });
    }
  },

  /** ［收起］：只收這台的「你已回覆」橫幅（D13） */
  dismiss(sosId: string): void {
    const dismissed = new Set(state.dismissed).add(sosId);
    saveMine(dismissed, LS_DISMISSED);
    set({ dismissed });
  },

  /** ［看面板］：全螢幕先收成橫幅（還沒回覆，橫幅上仍有［我知道了］） */
  minimize(sosId: string): void {
    set({ minimized: new Set(state.minimized).add(sosId) });
  },
};

/** 測試用：清狀態、停輪詢 */
export function __resetPanelSosForTest(): void {
  panelSos.stop();
}
